import { FC } from "react";
import { Button, Group, Modal, Select, Stack } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { addTeamToGroup } from "../../../../openapi/api-client";

interface Team {
  id: string;
  name: string;
}

interface AddTeamToGroupModalProps {
  groupId: string | null;
  onClose: () => void;
  competitionId: string;
  availableTeams: Team[];
}

export const AddTeamToGroupModal: FC<AddTeamToGroupModalProps> = ({
  groupId,
  onClose,
  competitionId,
  availableTeams,
}) => {
  const queryClient = useQueryClient();

  const form = useForm<{ teamId: string }>({
    initialValues: {
      teamId: "",
    },
  });

  const addTeamMutation = useMutation({
    mutationFn: addTeamToGroup,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["competition", { competitionId }],
      });
      onClose();
      form.reset();
    },
  });

  const handleSubmit = form.onSubmit(async (values) => {
    if (!groupId || !values.teamId) return;

    await addTeamMutation.mutateAsync({
      id: competitionId,
      groupId: groupId,
      teamId: values.teamId,
    });
  });

  return (
    <Modal
      opened={groupId !== null}
      onClose={onClose}
      title="Mannschaft zur Gruppe hinzufügen"
    >
      <form onSubmit={handleSubmit}>
        <Stack>
          <Select
            label="Mannschaft auswählen"
            placeholder="Mannschaft auswählen"
            data={availableTeams.map((t) => ({
              label: t.name,
              value: t.id,
            }))}
            searchable
            nothingFoundMessage="Keine verfügbare Mannschaft gefunden"
            {...form.getInputProps("teamId")}
          />

          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              Abbrechen
            </Button>
            <Button
              type="submit"
              loading={addTeamMutation.isPending}
              disabled={!form.values.teamId}
            >
              Hinzufügen
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};
